import { Clock, Duration, Effect, Layer, Schedule, Schema, Stream } from 'effect';
import { PDFDocument } from 'pdf-lib';

import { bboxFromRegionSpace, bboxToRegionSpace, bboxesIntersect } from './bbox';
import type { ParserExportResult, SnapshotPage } from './formatters';
import {
  DocumentSource,
  PageSource,
  ParserHttp,
  bytesRef,
  parserDependencyErrorCause,
  type ParseDocumentRef,
} from './parser-host';
import type { ParserJobHandle, ParserPollResult, ParserSpec, ParserUsage } from './parser';
import {
  makeParserCatalogService,
  makeParseProviderService,
  ParseProvider,
  ParserCatalog,
  ParseRuntimePortError,
  type ParseDescriptor,
  type ParserCatalogService,
  type ParseProviderService,
} from './parse-runtime';
import {
  CanonicalBlockSchema,
  type BlockType,
  type CanonicalBbox,
  type CanonicalBlock,
  type CanonicalParseResult,
} from './types';
import { toCanonicalParseResult } from './projections/okra-parse-output';

export type ParserCapability =
  | 'text'
  | 'layout'
  | 'tables'
  | 'bbox'
  | 'reading-order'
  | 'figures'
  | 'html'
  | 'markdown';

export const ParserCapabilitySchema = Schema.Literals([
  'text',
  'layout',
  'tables',
  'bbox',
  'reading-order',
  'figures',
  'html',
  'markdown',
]);

export type ParserNetworkPolicy = 'none' | { readonly allowedHosts: readonly string[] };

export const ParserNetworkPolicySchema = Schema.Union([
  Schema.Literal('none'),
  Schema.Struct({ allowedHosts: Schema.Array(Schema.String) }),
]);

export interface ParserDefinitionSpec extends ParserSpec {
  readonly provides: readonly ParserCapability[];
  readonly network: ParserNetworkPolicy;
}

const ParserPropertySchema = Schema.Struct({
  name: Schema.String,
  displayName: Schema.String,
  type: Schema.Literals(['string', 'number', 'boolean', 'options']),
  default: Schema.Unknown,
  description: Schema.optionalKey(Schema.String),
  options: Schema.optionalKey(
    Schema.Array(
      Schema.Struct({
        name: Schema.String,
        value: Schema.Union([Schema.String, Schema.Number, Schema.Boolean]),
      }),
    ),
  ),
});

export const ParserDefinitionSpecSchema = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  version: Schema.String,
  tier: Schema.Literals(['fast', 'standard', 'premium']),
  category: Schema.Literals(['ocr', 'entity', 'vlm', 'projection']),
  requiredEnvKeys: Schema.Array(Schema.String),
  inputs: Schema.Array(Schema.Literals(['pdf-bytes', 'page-images', 'nodes-json', 'gcs-prefix'])),
  outputs: Schema.Array(
    Schema.Literals(['parsed-result', 'entity-metadata', 'projection-receipt']),
  ),
  properties: Schema.Array(ParserPropertySchema),
  bestFor: Schema.optionalKey(Schema.Array(Schema.String)),
  supportsRegionExtract: Schema.optionalKey(Schema.Boolean),
  hooks: Schema.optionalKey(Schema.Array(Schema.String)),
  async: Schema.optionalKey(Schema.Boolean),
  pollStrategy: Schema.optionalKey(
    Schema.Struct({
      initialDelayMs: Schema.Finite,
      maxDelayMs: Schema.Finite,
      backoffMultiplier: Schema.Finite,
    }),
  ),
  provides: Schema.Array(ParserCapabilitySchema),
  network: ParserNetworkPolicySchema,
});

export interface ParsePageInput {
  readonly document: ParseDocumentRef;
  readonly page: number;
  readonly options?: Readonly<Record<string, unknown>>;
}

export interface ParseDocumentInput {
  readonly document: ParseDocumentRef;
  readonly options?: Readonly<Record<string, unknown>>;
  readonly concurrency?: number;
}

/** PDF Browser class-resolution input: locate `tag` on the given 1-based pages. */
export interface ResolveByTagInput {
  readonly document: ParseDocumentRef;
  readonly tag: string;
  readonly hint?: string;
  readonly pages: readonly number[];
}

export interface ParserDiagnostic {
  readonly level: 'info' | 'warning' | 'error';
  readonly code?: string;
  readonly message: string;
}

export const ParserDiagnosticSchema = Schema.Struct({
  level: Schema.Literals(['info', 'warning', 'error']),
  code: Schema.optionalKey(Schema.String),
  message: Schema.String,
});

export interface ParsedPage {
  readonly page: number;
  readonly blocks: readonly CanonicalBlock[];
  readonly width?: number;
  readonly height?: number;
  readonly usage?: ParserUsage;
  readonly raw?: unknown;
  readonly diagnostics?: readonly ParserDiagnostic[];
}

export const ParsedPageSchema = Schema.Struct({
  page: Schema.Int.check(Schema.isGreaterThan(0)),
  blocks: Schema.Array(CanonicalBlockSchema),
  width: Schema.optionalKey(Schema.Finite),
  height: Schema.optionalKey(Schema.Finite),
  usage: Schema.optionalKey(
    Schema.Struct({
      provider: Schema.optionalKey(Schema.String),
      model: Schema.optionalKey(Schema.String),
      actualModelId: Schema.optionalKey(Schema.String),
      parserProfile: Schema.optionalKey(Schema.String),
      inputTokens: Schema.optionalKey(Schema.Finite),
      outputTokens: Schema.optionalKey(Schema.Finite),
      totalTokens: Schema.optionalKey(Schema.Finite),
      costUsd: Schema.optionalKey(Schema.Finite),
      costEstimated: Schema.optionalKey(Schema.Boolean),
      currency: Schema.optionalKey(Schema.String),
    }),
  ),
  raw: Schema.optionalKey(Schema.Unknown),
  diagnostics: Schema.optionalKey(Schema.Array(ParserDiagnosticSchema)),
});

export type ParseEvent =
  | { readonly type: 'page'; readonly page: ParsedPage }
  | { readonly type: 'diagnostic'; readonly diagnostic: ParserDiagnostic };

export class ParserError extends Schema.TaggedError<ParserError>()('ParserError', {
  parserId: Schema.String,
  page: Schema.optionalKey(Schema.Int),
  code: Schema.String,
  retryable: Schema.Boolean,
  message: Schema.String,
}) {}

const parserErrorCauses = new WeakMap<ParserError, unknown>();

export function makeParserError(
  fields: ConstructorParameters<typeof ParserError>[0],
  cause?: unknown,
): ParserError {
  const error = new ParserError(fields);
  if (cause !== undefined) parserErrorCauses.set(error, cause);
  return error;
}

/** Restore the original cause only where a derived Promise adapter must reject it. */
export function parserErrorCause(error: unknown): unknown {
  const cause = error instanceof ParserError ? (parserErrorCauses.get(error) ?? error) : error;
  return parserDependencyErrorCause(cause);
}

export interface ParserDefinition<R = never> {
  readonly spec: ParserDefinitionSpec;
  readonly available: Effect.Effect<boolean, never, R>;
  readonly parsePage: (input: ParsePageInput) => Effect.Effect<ParsedPage, ParserError, R>;
  readonly parseDocument?: (input: ParseDocumentInput) => Stream.Stream<ParseEvent, ParserError, R>;
  /** Submit an async document job whose handle can survive a host restart. */
  readonly submitDocument?: (
    input: ParseDocumentInput,
  ) => Effect.Effect<ParserJobHandle, ParserError, R>;
  /** Poll an async document job once. */
  readonly pollDocument?: (
    handle: ParserJobHandle,
  ) => Effect.Effect<ParserPollResult, ParserError, R>;
  /** Detect already-parsed vendor data without host requirements or I/O. */
  readonly detect?: (data: unknown) => boolean;
  /** Normalize already-parsed vendor data without host requirements or I/O. */
  readonly normalize?: (data: unknown) => Effect.Effect<CanonicalParseResult, ParserError>;
  /** Parse a normalized region of one page when the parser supports region OCR. */
  readonly extractRegion?: (
    input: ParsePageInput & { readonly region: CanonicalBbox },
  ) => Effect.Effect<ParsedPage, ParserError, R>;
  /**
   * PDF Browser class resolution: locate `tag` on the given pages and return
   * the matched pages (blocks carry normalized bboxes; anchors are a projection).
   */
  readonly resolveByTag?: (
    input: ResolveByTagInput,
  ) => Effect.Effect<readonly ParsedPage[], ParserError, R>;
  readonly resolveType?: (rawType: string) => BlockType | undefined;
  readonly export?: (pages: SnapshotPage[]) => ParserExportResult;
}

export type RequirementsOf<D> = D extends ParserDefinition<infer R> ? R : never;

export type OfflineParser<D> =
  D extends ParserDefinition<infer R>
    ? [Extract<R, ParserHttp>] extends [never]
      ? D
      : never
    : never;

export function defineDocumentParser<R>(definition: ParserDefinition<R>): ParserDefinition<R> {
  return definition;
}

function descriptorFor(spec: ParserDefinitionSpec): ParseDescriptor {
  return {
    parserId: spec.id,
    providerId: spec.id,
    kind: spec.network === 'none' ? 'local' : 'byok',
  };
}

function countPdfPages(
  bytes: Uint8Array,
  parserId: string,
): Effect.Effect<number, ParseRuntimePortError> {
  return Effect.tryPromise({
    try: async () => (await PDFDocument.load(bytes, { ignoreEncryption: true })).getPageCount(),
    catch: () =>
      new ParseRuntimePortError({
        port: 'provider',
        operation: 'count_pages',
        code: 'invalid_pdf',
        retryable: false,
        parserId,
      }),
  });
}

function countDefinitionPdfPages(
  bytes: Uint8Array,
  parserId: string,
): Effect.Effect<number, ParserError> {
  return Effect.tryPromise({
    try: async () => (await PDFDocument.load(bytes, { ignoreEncryption: true })).getPageCount(),
    catch: (cause) =>
      makeParserError(
        {
          parserId,
          code: 'invalid_pdf',
          retryable: false,
          message: 'failed to read the PDF page count',
        },
        cause,
      ),
  });
}

function unavailableError(parserId: string, page: number): ParserError {
  return new ParserError({
    parserId,
    page,
    code: 'unavailable',
    retryable: false,
    message: `${parserId} is unavailable in this host`,
  });
}

function pollSchedule(spec: ParserDefinitionSpec): Schedule.Schedule<Duration.Duration> {
  const strategy = spec.pollStrategy ?? {
    initialDelayMs: 1_000,
    maxDelayMs: 30_000,
    backoffMultiplier: 2,
  };
  const initialDelayMs = Math.max(0, strategy.initialDelayMs);
  const maxDelayMs = Math.max(initialDelayMs, strategy.maxDelayMs);
  const backoffMultiplier = Math.max(1, strategy.backoffMultiplier);
  return Schedule.exponential(Duration.millis(initialDelayMs), backoffMultiplier).pipe(
    Schedule.modifyDelay(({ duration }) =>
      Effect.succeed(Duration.min(duration, Duration.millis(maxDelayMs))),
    ),
  );
}

function providerLayerForDefinitions<R>(
  definitions: ReadonlyMap<string, ParserDefinition<R>>,
): Layer.Layer<ParseProviderService, never, R> {
  return Layer.effect(
    ParseProvider,
    Effect.gen(function* () {
      const context = yield* Effect.context<R>();
      return makeParseProviderService({
        countPages: (descriptor, source) => countPdfPages(source, descriptor.parserId),
        parsePage: (descriptor, source, page) => {
          const definition = definitions.get(descriptor.parserId);
          if (!definition) {
            return Effect.fail(
              new ParseRuntimePortError({
                port: 'provider',
                operation: 'parse_page',
                code: 'parser_not_found',
                retryable: false,
                parserId: descriptor.parserId,
                page,
              }),
            );
          }
          const program = Effect.gen(function* () {
            const available = yield* definition.available;
            if (!available) return yield* unavailableError(definition.spec.id, page);
            return yield* definition.parsePage({
              document: bytesRef({
                documentId: `runtime:${definition.spec.id}`,
                bytes: source,
              }),
              page,
            });
          });
          return program.pipe(
            Effect.provide(context),
            Effect.map((parsed) => parsed.blocks),
            Effect.mapError(
              (error) =>
                new ParseRuntimePortError({
                  port: 'provider',
                  operation: 'parse_page',
                  code: error.code,
                  retryable: error.retryable,
                  parserId: error.parserId,
                  page: error.page ?? page,
                }),
            ),
          );
        },
      });
    }),
  );
}

/** Feed one definition into the S.5.16 parser catalog/provider runtime. */
export function parserLayer<R>(
  definition: ParserDefinition<R>,
): Layer.Layer<ParseProviderService | ParserCatalogService, never, R> {
  const descriptor = descriptorFor(definition.spec);
  const catalog = Layer.succeed(
    ParserCatalog,
    makeParserCatalogService((parserId) =>
      parserId === definition.spec.id
        ? Effect.succeed(descriptor)
        : Effect.fail(
            new ParseRuntimePortError({
              port: 'catalog',
              operation: 'resolve',
              code: 'parser_not_found',
              retryable: false,
              parserId,
            }),
          ),
    ),
  );
  const provider = providerLayerForDefinitions(
    new Map([[definition.spec.id, definition]]) as ReadonlyMap<string, ParserDefinition<R>>,
  );
  return Layer.mergeAll(catalog, provider);
}

/** Feed a parser set into one catalog/provider Layer for multi-parser hosts. */
export function parserCatalogLayer<Defs extends readonly ParserDefinition<any>[]>(
  definitions: Defs,
): Layer.Layer<ParseProviderService | ParserCatalogService, never, RequirementsOf<Defs[number]>> {
  type R = RequirementsOf<Defs[number]>;
  const byId = new Map(definitions.map((definition) => [definition.spec.id, definition])) as Map<
    string,
    ParserDefinition<R>
  >;
  const catalog = Layer.succeed(
    ParserCatalog,
    makeParserCatalogService((parserId) => {
      const definition = byId.get(parserId);
      return definition
        ? Effect.succeed(descriptorFor(definition.spec))
        : Effect.fail(
            new ParseRuntimePortError({
              port: 'catalog',
              operation: 'resolve',
              code: 'parser_not_found',
              retryable: false,
              parserId,
            }),
          );
    }),
  );
  return Layer.mergeAll(catalog, providerLayerForDefinitions(byId));
}

export function runParsePage<R, RH>(
  definition: ParserDefinition<R>,
  input: ParsePageInput,
  host: Layer.Layer<RH> & ([R] extends [RH] ? unknown : never),
): Effect.Effect<ParsedPage, ParserError> {
  return definition.parsePage(input).pipe(Effect.provide(host)) as Effect.Effect<
    ParsedPage,
    ParserError
  >;
}

/** Execute a whole document without introducing a second lifecycle implementation. */
export function runParseDocument<R, RH>(
  definition: ParserDefinition<R>,
  input: ParseDocumentInput,
  host: Layer.Layer<RH> & ([R] extends [RH] ? unknown : never),
): Effect.Effect<CanonicalParseResult, ParserError> {
  const program = Effect.gen(function* () {
    const startedAt = yield* Clock.currentTimeMillis;
    const available = yield* definition.available;
    if (!available) return yield* unavailableError(definition.spec.id, 1);

    let pages: readonly ParsedPage[];
    if (definition.parseDocument) {
      const events = yield* Stream.runCollect(definition.parseDocument(input));
      pages = events.flatMap((event) => (event.type === 'page' ? [event.page] : []));
    } else if (definition.submitDocument && definition.pollDocument) {
      const handle = yield* definition.submitDocument(input);
      const polled = yield* Effect.repeat(definition.pollDocument(handle), {
        schedule: pollSchedule(definition.spec),
        until: (result) => result.done,
      });
      if (!polled.output?.parsedResult) {
        return yield* makeParserError({
          parserId: definition.spec.id,
          code: 'empty_job_output',
          retryable: false,
          message: `${definition.spec.id} completed without a parsed result`,
        });
      }
      return polled.output.parsedResult;
    } else {
      const pageCount =
        input.document.pageCount ??
        (yield* Effect.gen(function* () {
          const document = input.document;
          if (document.kind !== 'storage') {
            return yield* countDefinitionPdfPages(document.bytes, definition.spec.id);
          }
          const source = yield* DocumentSource;
          const bytes = yield* source.bytes(document).pipe(
            Effect.mapError((cause) =>
              makeParserError(
                {
                  parserId: definition.spec.id,
                  code: cause.code,
                  retryable: cause.retryable,
                  message: 'failed to materialize the document source',
                },
                cause,
              ),
            ),
          );
          return yield* countDefinitionPdfPages(bytes, definition.spec.id);
        }));
      pages = yield* Effect.forEach(
        Array.from({ length: pageCount }, (_, index) => index + 1),
        (page) =>
          definition.parsePage({
            document: input.document,
            page,
            ...(input.options === undefined ? {} : { options: input.options }),
          }),
        { concurrency: input.concurrency ?? 1 },
      );
    }
    const durationMs = (yield* Clock.currentTimeMillis) - startedAt;
    return toCanonicalParseResult(pages, definition.spec, { durationMs });
  });
  return program.pipe(Effect.provide(host)) as Effect.Effect<CanonicalParseResult, ParserError>;
}

// Compile-time proof: the memory pack used below cannot satisfy ParserHttp.
// Kept here (instead of a test-only file) so `pnpm typecheck` enforces the invariant.
function proveMemoryHostBoundary(
  networkDefinition: ParserDefinition<ParserHttp>,
  input: ParsePageInput,
): void {
  const memoryHost = null as unknown as Layer.Layer<PageSource>;
  // @ts-expect-error ParserHttp is intentionally absent from the memory host.
  runParsePage(networkDefinition, input, memoryHost);
}
void proveMemoryHostBoundary;

// ── Tag matching and region projection (pure helpers) ─────────────

/** Normalize a PDF Browser class tag ('income-statement') for text matching. */
export function normalizeTagText(value: string): string {
  return value.trim().toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ');
}

function blockText(block: CanonicalBlock): string {
  const own = [block.label, block.value].filter((value): value is string => Boolean(value));
  const children = (block.children ?? []).map(blockText);
  return [...own, ...children].filter(Boolean).join('\n');
}

/** A block matches when its text (or a descendant's) contains the normalized tag. */
export function blockMatchesTag(block: CanonicalBlock, normalizedTag: string): boolean {
  return normalizeTagText(blockText(block)).includes(normalizedTag);
}

function filterBlockByTag(
  block: CanonicalBlock,
  normalizedTag: string,
): CanonicalBlock | undefined {
  if (blockMatchesTag(block, normalizedTag)) return block;
  const children = (block.children ?? []).flatMap((child) => {
    const filtered = filterBlockByTag(child, normalizedTag);
    return filtered ? [filtered] : [];
  });
  return children.length > 0 ? { ...block, children } : undefined;
}

/**
 * Keep only blocks matching the tag, dropping pages with no matches.
 * Used by `resolveByTag` implementations (PDF Browser class resolution).
 */
export function filterPagesByTag(pages: readonly ParsedPage[], tag: string): readonly ParsedPage[] {
  const normalizedTag = normalizeTagText(tag);
  if (!normalizedTag) return [];
  return pages.flatMap((page) => {
    const blocks = page.blocks.flatMap((block) => {
      const filtered = filterBlockByTag(block, normalizedTag);
      return filtered ? [filtered] : [];
    });
    return blocks.length > 0 ? [{ ...page, blocks }] : [];
  });
}

function clipBlockToRegion(
  block: CanonicalBlock,
  region: CanonicalBbox,
): CanonicalBlock | undefined {
  const clippedBbox = block.bbox === undefined ? undefined : bboxToRegionSpace(block.bbox, region);
  const children = (block.children ?? []).flatMap((child) => {
    const clipped = clipBlockToRegion(child, region);
    return clipped ? [clipped] : [];
  });
  if (clippedBbox === undefined && children.length === 0) return undefined;
  return {
    ...block,
    ...(clippedBbox === undefined ? {} : { bbox: clippedBbox }),
    ...(children.length === 0 ? {} : { children }),
  };
}

/**
 * Keep blocks intersecting the region with bboxes re-expressed in region
 * space — the text-layer `extractRegion` projection.
 */
export function clipPageToRegion(page: ParsedPage, region: CanonicalBbox): ParsedPage {
  const blocks = page.blocks.flatMap((block) => {
    const clipped = clipBlockToRegion(block, region);
    return clipped ? [clipped] : [];
  });
  return { ...page, blocks };
}

function reprojectBlockFromRegion(block: CanonicalBlock, region: CanonicalBbox): CanonicalBlock {
  return {
    ...block,
    ...(block.bbox === undefined ? {} : { bbox: bboxFromRegionSpace(block.bbox, region) }),
    ...(block.children === undefined
      ? {}
      : { children: block.children.map((child) => reprojectBlockFromRegion(child, region)) }),
  };
}

/**
 * Re-project every block bbox from region space back into page space — the
 * VLM `extractRegion` projection after parsing a cropped page.
 */
export function reprojectPageFromRegion(page: ParsedPage, region: CanonicalBbox): ParsedPage {
  return {
    ...page,
    blocks: page.blocks.map((block) => reprojectBlockFromRegion(block, region)),
  };
}

/** Region-intersection guard shared by region-aware implementations. */
export function pageIntersectsRegion(bbox: CanonicalBbox, region: CanonicalBbox): boolean {
  return bboxesIntersect(bbox, region);
}
